/*
 * Measurement Point Result List - list the answers from the most recent result
 */

import * as React from 'react';
import { TFunction } from 'i18next';

import { ListGroup, ListGroupItem, Col, Button, Row } from 'react-bootstrap';
import {
    ImeasurementPointResult,
    ImeasurementPointAnswer,
    ImeasurementPoint
} from '../../models';
import { getMeasurementPointList } from '../../actions/manageMeasurementPointListsActions';
import { constants } from '../../constants/constants';
import { initialMeasurementPoint } from '../../reducers/initialState';
import { clearHistoricalResultID } from '../../actions/measurementPointResultsActions';
import moment from 'moment';

interface Props {
    selectedItem: ImeasurementPointResult;
    selectedMeasurementPointResultID: string;
    colorButton: string;
    t: TFunction;
    toggleModal: () => void;
    getMeasurementPointList: typeof getMeasurementPointList;
    measurementPointsByID: { [key: string]: ImeasurementPoint };
    locationString: string;
    clearHistoricalResultID: typeof clearHistoricalResultID;
    getMeasurementPointListResult: (
        id: string
    ) => Promise<ImeasurementPointResult>;
    loading: boolean;
}

const AnswerListItem = (
    mpAnswer: ImeasurementPointAnswer,
    measurementPoint: ImeasurementPoint,
    t: TFunction
) => {
    let AnswerElement = () => <div>no answer</div>;
    if (mpAnswer.numericValue !== undefined) {
        AnswerElement = () => <div>{mpAnswer.numericValue}</div>;
    }
    if (mpAnswer.pass !== undefined) {
        if (mpAnswer.pass === constants.measurementPointPassFailTypes.fail) {
            AnswerElement = () => (
                <div
                    style={{
                        textTransform: 'uppercase',
                        color: constants.colors.red
                    }}
                >
                    {t('fail')}
                </div>
            );
        } else if (
            mpAnswer.pass === constants.measurementPointPassFailTypes.pass
        ) {
            AnswerElement = () => (
                <div
                    style={{
                        textTransform: 'uppercase',
                        color: constants.colors.green
                    }}
                >
                    {t('pass')}
                </div>
            );
        } else {
            AnswerElement = () => (
                <div style={{ textTransform: 'uppercase' }}>{t('N/A')}</div>
            );
        }
    }
    if (mpAnswer.textValue !== undefined) {
        AnswerElement = () => <div>{mpAnswer.textValue}</div>;
    }
    if (mpAnswer.dateValue !== undefined) {
        AnswerElement = () => (
            <div>
                {moment
                    .utc(mpAnswer.dateValue)
                    .local(true)
                    .format('DD-MM-YY')}
            </div>
        );
    }

    if (
        mpAnswer.measurementPointSelectOptionID !== undefined &&
        measurementPoint.selectOptions
    ) {
        const { selectOptions } = measurementPoint;
        const selectedOption = selectOptions.find(
            option => option.id === mpAnswer.measurementPointSelectOptionID
        ) || { label: '' };

        AnswerElement = () => <div>{selectedOption.label}</div>;
    }

    return (
        <ListGroupItem key={mpAnswer.measurementPointID}>
            <Row>
                <Col xs={8}>
                    {measurementPoint.label} {measurementPoint.guideText}
                </Col>
                <Col
                    xs={4}
                    style={{ fontWeight: 'bolder', textAlign: 'right' }}
                >
                    <AnswerElement />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <small>{mpAnswer.notes}</small>
                </Col>
            </Row>
        </ListGroupItem>
    );
};

/*
 * Every state of this drawer that is not a list of answers is one line of text plus the Done
 * button: nothing was inspected, nothing has loaded yet, or the status was typed in by hand.
 */
const ResultMessage = ({
    message,
    colorButton,
    doneLabel,
    onDone
}: {
    message: string;
    colorButton: string;
    doneLabel: string;
    onDone: () => void;
}) => (
    <div>
        <h4 style={{ padding: '15px' }}>{message}</h4>
        <Col xs={12} className="form-buttons text-right">
            <Button bsStyle={colorButton} type="button" onClick={onDone}>
                {doneLabel}
            </Button>
        </Col>
    </div>
);

export class MPResultList extends React.Component<Props, {}> {
    componentDidMount() {
        const { selectedItem } = this.props;
        if (selectedItem.manualOverride === true) {
            return; //  do not get mplist or the result
        }
        if (selectedItem.measurementPointAnswers.length === 0) {
            // MMG-4361: an asset can carry a status with no saved result behind it - overriding the
            // status nulls the install base's latest result. Requesting an empty ID only produced a
            // 404 toast over an empty drawer, so render the empty message instead.
            if (!selectedItem.id) {
                return;
            }
            this.props
                .getMeasurementPointListResult(selectedItem.id)
                .then(result => {
                    if (result && result.measurementPointListID) {
                        this.props.getMeasurementPointList(
                            result.measurementPointListID
                        );
                    }
                })
                // the action already reported the failure; this keeps it from also surfacing as an
                // unhandled rejection
                .catch(() => undefined);
        } else if (selectedItem.measurementPointListID) {
            this.props.getMeasurementPointList(
                selectedItem.measurementPointListID
            );
        }
    }

    render() {
        const { t, selectedItem } = this.props;
        const doneLabel = t('common:doneButton');
        if (selectedItem.manualOverride === true) {
            return (
                <ResultMessage
                    message={t(
                        'No measurement points.  Device status was manually added.'
                    )}
                    colorButton={this.props.colorButton}
                    doneLabel={doneLabel}
                    onDone={this.props.toggleModal}
                />
            );
        }
        if (this.props.loading) {
            return (
                <ResultMessage
                    message={t('Loading...')}
                    colorButton={this.props.colorButton}
                    doneLabel={doneLabel}
                    onDone={this.props.toggleModal}
                />
            );
        }
        const answerItems = selectedItem.measurementPointAnswers
            .map(mpAnswer => {
                const measurementPoint =
                    this.props.measurementPointsByID[
                        mpAnswer.measurementPointID
                    ] || initialMeasurementPoint;
                if (measurementPoint.id.length === 0) {
                    // jfbloom22 removed this 5/2020 because it will be 0 on initial render.
                    // a better solution is to use a selector to grab all the MPs for this list
                    // and wait to render until we have some MPs, this requires two loops because
                    // each MP is related to a MeasurementPointTab
                    // console.error(
                    //   'missing measurement point',
                    //   mpAnswer.measurementPointID
                    // );
                    return null;
                }
                return AnswerListItem(mpAnswer, measurementPoint, t);
            })
            .filter(item => item !== null);
        const onDone = () => {
            this.props.toggleModal();
            this.props.clearHistoricalResultID();
        };
        /*
         * MMG-4361: nothing renderable means nothing to show - say so, rather than leaving the
         * drawer blank behind an error toast. Either no inspection was ever saved for this asset,
         * or every question its result answered has since been removed from the list.
         */
        if (answerItems.length === 0) {
            return (
                <ResultMessage
                    message={t('No inspection data to show for this asset.')}
                    colorButton={this.props.colorButton}
                    doneLabel={doneLabel}
                    onDone={onDone}
                />
            );
        }
        return (
            <div>
                <h5 style={{ paddingLeft: '15px', paddingBottom: '15px' }}>
                    {this.props.locationString}
                </h5>
                <ListGroup>{answerItems}</ListGroup>
                <Col xs={12} className="form-buttons text-right">
                    <Button
                        bsStyle={this.props.colorButton}
                        type="button"
                        onClick={onDone}
                    >
                        {doneLabel}
                    </Button>
                </Col>
            </div>
        );
    }
}
